import Image from 'next/image'
import React, { PropsWithChildren, useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Absolute } from '../../parts/bases/Absolute'
import { Center } from '../../parts/bases/Center'
import { Clickable } from '../../parts/bases/Clickable'
import { HStack } from '../../parts/bases/HStack'
import { Space } from '../../parts/bases/Space'
import { Text } from '../../parts/bases/Text'
import { VStack } from '../../parts/bases/VStack'
import { PinkNeonButton } from '../../parts/buttons/PinkNeonButton'
import { EnergyGauge } from '../../parts/commons/EnergyGauge'
import { GlobalNavigationLayout } from '../../parts/navigations/GlobalNavigationLayout'
import { InOperationCheckModal } from '../../parts/modals/InOperationCheckModal'
import { Fixed } from '../../parts/bases/Fixed'
import { MegaSparkingLabel } from '../../parts/commons/MegaSparkingLabel'
import megasparkingBanner from '@/public/images/megasparking_01.png'
import megasparkingAM from '@/public/images/megasparking_03.png'
import { VideoPlayer } from '../../parts/commons/VideoPlayer'

export type Props = {
  viewType: 'READY' | 'IN_OPERATION' | 'IN_OPERATION_PLAY' | null
  loading: boolean
  title: string
  imageUrl: string
  movie: string
  arcadeImage?: string
  machineImage: string
  machineImageInOperation: string
  rule: string
  category: string
  teras?: number
  maxEnergy: number
  energy: number
  extractedEnergy: number
  megaSparkUpcoming: boolean
  feverSparkRemain: number | null
  onClickPlay: () => void
  visibleHelp: boolean
  helpDescription: string
  helpHowTo: string
  onClickHelp: () => void
  onClickHelpClose: () => void
  visibleInOperationCheckModal: boolean
  onClickWait: () => void
  onClickSearch: () => void
}

export const Layout: React.FC<PropsWithChildren<Props>> = (props) => {
  const [isInit, setInit] = useState(true)

  useEffect(() => {
    // beltScrollInitクラスのアニメーションが終わるタイミングで初期フラグをOFF
    setTimeout(() => {
      setInit(false)
    }, 10000)
    return () => {
      setInit(true)
    }
  }, [])

  return props.loading || !props.viewType ? (
    <></>
  ) : props.viewType === 'IN_OPERATION' || props.viewType === 'IN_OPERATION_PLAY' ? (
    <InOperationView {...props} isInit={isInit} />
  ) : (
    <PlayView {...props} isInit={isInit} />
  )
}

const PlayView = (props: Props & { isInit: boolean }) => {
  return (
    <VStack
      className="bg-cover bg-top bg-no-repeat"
      style={{ backgroundImage: `url('${props.machineImage}')` }}
    >
      <GlobalNavigationLayout visibleBack title={props.title}>
        <Center className="relative aspect-[390/520] w-full overflow-hidden bg-black bg-opacity-30">
          <VideoPlayer poster={props.imageUrl} src={props.movie} />
          {props.arcadeImage ? (
            <Absolute className="bottom-0 left-0">
              <Center className={`relative aspect-square w-44`}>
                <Image layout="fill" src={props.arcadeImage} alt={props.arcadeImage} />
                <Absolute className="bottom-[30px] left-[34px]">
                  <EnergyGauge
                    maxEnergy={props.maxEnergy}
                    energy={props.energy}
                    extractedEnergy={props.extractedEnergy}
                    feverSparkRemain={props.feverSparkRemain}
                    height={80}
                  />
                </Absolute>
              </Center>
              {props.feverSparkRemain !== null && props.feverSparkRemain !== undefined && (
                <Absolute className="w-full">
                  <Image src={megasparkingAM} className="h-full w-full" alt="" />
                </Absolute>
              )}
            </Absolute>
          ) : null}
          {props.teras !== undefined ? (
            <Absolute
              className={`bottom-3 right-3 items-center justify-center rounded-full bg-black/40 px-3 py-1 backdrop-blur-[2.8px]`}
            >
              <HStack>
                <Center className="mr-2">
                  <Image width={30} height={30} src={'/images/teras.png'} alt={'Teras'} />
                </Center>
                <Text
                  className={`text-2xl font-bold ${
                    props.megaSparkUpcoming || props.feverSparkRemain != null
                      ? 'text-akiverse-yellow'
                      : 'text-white'
                  }`}
                >
                  {props.teras}
                </Text>
              </HStack>
            </Absolute>
          ) : null}
          <RuleView rule={props.rule} category={props.category} />
          {props.megaSparkUpcoming && <MegaSparkBelt isInit={props.isInit} />}
          {props.visibleHelp ? <HelpDialog {...props} /> : null}
        </Center>
        <VStack className="relative aspect-[1125/630] w-full">
          <Absolute className="w-full flex-col items-center">
            <Center className="p-6">
              <PinkNeonButton onClick={props.onClickPlay}>
                <Center className="px-10">
                  <Text className="font-bold text-white">
                    <FormattedMessage id="ArcadeDetailPage.PlayButton" />
                  </Text>
                </Center>
              </PinkNeonButton>
            </Center>
            <HowToArea onClickHelp={props.onClickHelp} visibleHelp={props.visibleHelp} />
          </Absolute>
        </VStack>
      </GlobalNavigationLayout>

      {props.feverSparkRemain && <MegaSparkingView feverSparkRemain={props.feverSparkRemain} />}

      {props.visibleInOperationCheckModal ? (
        <InOperationCheckModal onClickWait={props.onClickWait} onClickSearch={props.onClickSearch} />
      ) : null}
    </VStack>
  )
}

const InOperationView = (props: Props & { isInit: boolean }) => {
  return (
    <GlobalNavigationLayout visibleBack title={props.title}>
      <Center className="h-[calc(100dvh_-_176px_-_112px_-_env(safe-area-inset-top)_-_env(safe-area-inset-bottom))] pt-28">
        <VStack
          className="h-screen overflow-hidden bg-top bg-no-repeat"
          style={{
            backgroundImage: `url('${props.machineImageInOperation}')`,
            backgroundSize: 'cover',
          }}
        >
          <Absolute className="top-10 mt-[calc(env(safe-area-inset-yop))] h-full w-full overflow-hidden">
            {props.visibleHelp ? <HelpDialog {...props} /> : null}
          </Absolute>
          <Fixed className="bottom-40 mb-[calc(env(safe-area-inset-bottom))]">
            <VStack className="relative mb-20 h-full w-screen">
              <Absolute className="w-full flex-col items-center">
                <Center className="p-6">
                  {props.viewType === 'IN_OPERATION' ? (
                    <Center className="mb-5 h-12">
                      <OtherPlayBelt isInit={props.isInit} />
                    </Center>
                  ) : (
                    <PinkNeonButton onClick={props.onClickPlay}>
                      <Center className="px-10">
                        <Text className="font-bold text-white">
                          <FormattedMessage id="ArcadeDetailPage.PlayButton" />
                        </Text>
                      </Center>
                    </PinkNeonButton>
                  )}
                </Center>
                <HowToArea onClickHelp={props.onClickHelp} visibleHelp={props.visibleHelp} />
              </Absolute>
            </VStack>
          </Fixed>

          {props.feverSparkRemain && <MegaSparkingView feverSparkRemain={props.feverSparkRemain} />}

          {props.visibleInOperationCheckModal ? (
            <InOperationCheckModal onClickWait={props.onClickWait} onClickSearch={props.onClickSearch} />
          ) : null}
        </VStack>
      </Center>
    </GlobalNavigationLayout>
  )
}

const MegaSparkingView = (props: { feverSparkRemain: number }) => {
  return (
    <Absolute className="top-1/2 w-full -translate-y-3/4">
      <Center className="h-full w-full">
        <Image src={megasparkingBanner} />
      </Center>
      <Absolute className="bottom-16 w-full">
        <MegaSparkingLabel feverSparkRemain={props.feverSparkRemain} size="L" className="mx-auto" />
      </Absolute>
    </Absolute>
  )
}

const HelpDialog = (props: Props) => {
  return (
    <Absolute className="top-10 z-10 w-full p-5">
      <VStack className="relative px-4 pb-4 pt-3">
        <Absolute className="left-0 top-0 h-full w-full flex-col">
          <VStack className="aspect-[1053/120] w-full bg-[url('/images/helpPopupTop.png')] bg-contain bg-no-repeat" />
          <VStack className="w-full flex-1 bg-[url('/images/helpPopupMiddle.png')]  bg-contain bg-repeat-y" />
          <VStack className="aspect-[1053/141] w-full bg-[url('/images/helpPopupBottom.png')]  bg-contain bg-no-repeat" />
        </Absolute>
        <VStack className="w-full rounded-md bg-opacity-60 p-2">
          <span className="text-base font-bold text-neon-blue">Description</span>
          <span className="text-sm text-white">{props.helpDescription}</span>
          <span className="mt-3 text-base font-bold text-neon-blue">How to Play</span>
          <span className="whitespace-pre-wrap text-sm text-white">{props.helpHowTo}</span>
          <Absolute className="right-2 top-2">
            <Clickable onClick={props.onClickHelpClose}>
              <Image src="/images/helpCloseIcon.png" width={24} height={24} alt="help" />
            </Clickable>
          </Absolute>
        </VStack>
      </VStack>
    </Absolute>
  )
}

const HowToArea = (props: { onClickHelp: () => void; visibleHelp: boolean }) => {
  return (
    <HStack className="w-full items-center justify-center">
      <Clickable onClick={props.onClickHelp}>
        <Center
          className={`h-10 min-w-[140px] border
          ${
            props.visibleHelp
              ? 'border-white bg-akiverse-pink bg-opacity-80'
              : 'border-akiverse-pink bg-black bg-opacity-50'
          }
          font-bold text-white`}
        >
          HOW TO
        </Center>
      </Clickable>
      <div className="w-5" />
      <Clickable onClick={props.onClickHelp} disabled>
        <VStack className="h-10 min-w-[140px] flex-col justify-start border border-akiverse-pink bg-black font-bold text-white opacity-50">
          DATA
        </VStack>
        <Absolute className="bottom-1 w-full items-center justify-center">
          <Text className="text-xs text-neon-blue">COMING SOON</Text>
        </Absolute>
      </Clickable>
    </HStack>
  )
}

const RuleView = (props: { rule: string; category: string }) => {
  return (
    <Absolute className="left-0 top-0 w-full">
      <HStack className="w-full items-center p-4">
        <Text className="flex h-5 items-center justify-center rounded-full bg-purple-500 px-3 text-xs font-bold text-white">
          {props.rule}
        </Text>
        <Text className="ml-2 text-xs font-bold text-white">{props.category}</Text>
        <Space />
      </HStack>
    </Absolute>
  )
}

const MegaSparkBelt = (props: { isInit: boolean }) => {
  return (
    <Absolute className="left-0 top-12 block w-full overflow-hidden whitespace-nowrap bg-[#FCFF54] opacity-80">
      <HStack
        className={`inline-block h-[35px] items-center gap-x-2 ${
          props.isInit ? 'beltScrollInit' : 'beltScroll'
        }`}
      >
        <Text className="flex h-5 items-center justify-center pl-5 text-xl font-bold text-akiverse-pink">
          MEGA SPARK upcoming!!
        </Text>
        <Center className="w-6">
          <Image
            className="flex items-center justify-center"
            width={24}
            height={24}
            src={'/images/terasTypeResult.png'}
            alt={'Teras'}
            objectFit="contain"
          />
        </Center>
        <Text className="flex h-5 items-center justify-center text-xl font-bold text-akiverse-pink">
          Try to get additional Teras!!
        </Text>
      </HStack>
    </Absolute>
  )
}

const OtherPlayBelt = (props: { isInit: boolean }) => {
  return (
    <Absolute className="left-0 top-12 block min-h-[48px] w-full items-center overflow-hidden whitespace-nowrap bg-[#EA00FF]/70">
      <HStack
        className={`inline-block min-h-[48px] items-center ${props.isInit ? 'beltScrollInit' : 'beltScroll'}`}
      >
        <HStack className="items-center gap-x-2 pl-5">
          <Center className="h-12 w-12">
            <Image
              className="flex items-center justify-center"
              width={48}
              height={48}
              src={'/images/iconInoperation.png'}
              alt={'Rom'}
              objectFit="contain"
            />
          </Center>
          <Text className="flex h-5 items-center justify-center text-xl font-bold text-white">
            In operation. This AM is currently in use by someone else.
          </Text>
        </HStack>
      </HStack>
    </Absolute>
  )
}
